/**
 * The calendar day (`YYYY-MM-DD`) an instant falls on in a time zone, for the
 * web and the phone alike (R2 of docs/plans/qa-fixes-2.md: today is the
 * workspace's own day).
 *
 * It lives here and not in `apps/mobile/src/lib` because it needs an `Intl`
 * date formatter, which the app trees never construct (the format guard in
 * packages/ui/src/theme/format.enforcement.test.ts). It reads `format`, not
 * `formatToParts`: Hermes' `formatToParts` has answered no parts on the device
 * (see `format.ts`). `en-CA` writes a date as `YYYY-MM-DD`; anything else that
 * comes back, a zone the engine refuses, or no zone at all, reads the engine's
 * own local day, as the server's `zoneDay` falls back rather than refuse.
 */
const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

/** The day an instant falls on by this engine's own clock. */
export const localDayOf = (instant: Date): string =>
  `${pad(instant.getFullYear(), 4)}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}`;

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

export const dayInZone = (instant: Date, zone: string | null): string => {
  const named = zone?.trim() ?? '';
  if (named === '') return localDayOf(instant);
  try {
    if (named === Intl.DateTimeFormat().resolvedOptions().timeZone) return localDayOf(instant);
    const written = new Intl.DateTimeFormat('en-CA', { timeZone: named, year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
    return ISO_DAY.test(written) ? written : localDayOf(instant);
  } catch {
    return localDayOf(instant);
  }
};
